/**
 * TrackVisitDialog — reusable dialog for logging a site visit against a contact.
 *
 * Lives in two places: the Contact profile and any Opportunity record where a
 * contact has been linked. Either entry point passes in the contact_id + name;
 * we route the saved visit against the contact regardless of where the dialog
 * was opened, per the feature spec.
 *
 * Distance Matrix is called on-demand via "Calculate", so the user always sees
 * the km + cost preview *before* hitting Save. Once a visit is saved, the
 * onSaved callback fires so the parent can refresh its visit list.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Loader2, MapPin, Calculator, Save } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

function _localDatetimeNow() {
  // Returns YYYY-MM-DDThh:mm in the user's local timezone — the format
  // <input type="datetime-local"> expects. We store visit_date as ISO UTC
  // server-side; conversion happens on submit.
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function TrackVisitDialog({
  open,
  onOpenChange,
  contactId,
  contactName,
  opportunityId = null,
  onSaved,
}) {
  const { get, post } = useApi();
  const [rate, setRate] = useState(null);
  const [startAddress, setStartAddress] = useState('');
  const [endAddress, setEndAddress] = useState('');
  const [note, setNote] = useState('');
  const [whenLocal, setWhenLocal] = useState(_localDatetimeNow());
  const [distance, setDistance] = useState(null); // { distance_km, duration_minutes }
  const [calcLoading, setCalcLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset + prefill when dialog opens
  useEffect(() => {
    if (!open) return;
    setStartAddress('');
    setEndAddress('');
    setNote('');
    setWhenLocal(_localDatetimeNow());
    setDistance(null);

    (async () => {
      try {
        const r = await get('/mileage/rate');
        setRate(r);
      } catch (e) {
        console.error('mileage rate fetch failed', e);
      }
      try {
        const data = await get(`/mileage/contact-address/${contactId}`);
        if (data?.address) setEndAddress(data.address);
      } catch (e) {
        // best-effort prefill — non-blocking
      }
    })();
  }, [open, contactId, get]);

  const activeRate = rate?.active_rate_per_km || 0;
  const activeType = rate?.active_rate_type || 'cra';
  const costPreview = distance ? distance.distance_km * activeRate : 0;

  async function handleCalculate() {
    if (!startAddress.trim() || !endAddress.trim()) {
      toast.error('Enter both start and end addresses');
      return;
    }
    setCalcLoading(true);
    try {
      const data = await post('/mileage/calculate-distance', {
        start_address: startAddress.trim(),
        end_address: endAddress.trim(),
      });
      setDistance(data);
    } catch (e) {
      toast.error(e?.message || 'Could not calculate distance');
      setDistance(null);
    } finally {
      setCalcLoading(false);
    }
  }

  async function handleSave() {
    if (!distance) {
      toast.error('Calculate the distance first');
      return;
    }
    setSaving(true);
    try {
      const isoDate = whenLocal
        ? new Date(whenLocal).toISOString()
        : new Date().toISOString();
      const payload = {
        contact_id: contactId,
        opportunity_id: opportunityId || null,
        start_address: startAddress.trim(),
        end_address: endAddress.trim(),
        distance_km: distance.distance_km,
        note: note.trim(),
        visit_date: isoDate,
        rate_per_km: activeRate,
        rate_type: activeType,
      };
      await post('/mileage/visits', payload);
      toast.success('Visit logged');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.message || 'Failed to save visit');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="track-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#C5A059]" />
            Track Visit
          </DialogTitle>
          <DialogDescription>
            Logging a visit to <strong>{contactName || 'this contact'}</strong>.
            Distance is computed by Google Maps; you can review km and cost before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="start-address">Start address</Label>
            <Input
              id="start-address"
              data-testid="visit-start-address"
              placeholder="e.g. 1234 W Broadway, Vancouver"
              value={startAddress}
              onChange={(e) => { setStartAddress(e.target.value); setDistance(null); }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="end-address">End / client address</Label>
            <Input
              id="end-address"
              data-testid="visit-end-address"
              placeholder="Client site"
              value={endAddress}
              onChange={(e) => { setEndAddress(e.target.value); setDistance(null); }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="visit-date">Date & time</Label>
              <Input
                id="visit-date"
                data-testid="visit-date"
                type="datetime-local"
                value={whenLocal}
                onChange={(e) => setWhenLocal(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Active rate</Label>
              <div className="flex items-center gap-2 h-9 text-sm">
                <Badge variant="outline" className="rounded-sm">
                  {activeType === 'custom' ? 'Custom' : 'CRA'}
                </Badge>
                <span data-testid="visit-active-rate">
                  ${activeRate?.toFixed?.(4) || '—'}/km
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="visit-note">Note (optional)</Label>
            <Textarea
              id="visit-note"
              data-testid="visit-note"
              placeholder="Site visit, delivery, follow-up meeting…"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleCalculate}
            disabled={calcLoading}
            className="w-full"
            data-testid="visit-calculate-btn"
          >
            {calcLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Calculator className="h-4 w-4 mr-2" />
            )}
            Calculate distance & cost
          </Button>

          {distance && (
            <div
              data-testid="visit-preview"
              className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm"
            >
              <div className="flex items-center justify-between">
                <span className="text-stone-600">Distance</span>
                <span className="font-medium text-emerald-800">
                  {distance.distance_km.toFixed(2)} km
                  {distance.duration_minutes && (
                    <span className="ml-2 text-xs text-stone-500">
                      (~{Math.round(distance.duration_minutes)} min)
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-stone-600">Cost @ ${activeRate.toFixed(4)}/km</span>
                <span className="font-semibold text-emerald-900">
                  ${costPreview.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="visit-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!distance || saving}
            data-testid="visit-save-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
